import { Button, Checkbox, Stack, Text } from "@mantine/core";
import { useId, useState } from "react";

import {
  reportColumnIds,
  reportColumnLabel,
  type ReportColumnId,
} from "./columns.js";

export interface ColumnChooserProps {
  readonly columnIds: readonly ReportColumnId[];
  readonly isSaving?: boolean;
  readonly onChange: (columnIds: readonly ReportColumnId[]) => void;
  readonly unavailableColumnIds: readonly ReportColumnId[];
}

export function ColumnChooser({
  columnIds,
  isSaving = false,
  onChange,
  unavailableColumnIds,
}: ColumnChooserProps) {
  const unavailable = new Set(unavailableColumnIds);
  const [opened, setOpened] = useState(false);
  const panelId = useId();

  return (
    <div className="column-chooser">
      <Button
        aria-controls={panelId}
        aria-expanded={opened}
        loading={isSaving}
        onClick={() => setOpened((current) => !current)}
        size="compact-sm"
        variant="default"
      >
        Choose columns
      </Button>
      {opened ? (
        <div aria-label="Report columns" className="column-chooser__panel" id={panelId} role="group">
          <Stack gap="xs" p="xs">
            <Text fw={600} size="sm">Visible columns</Text>
            {reportColumnIds.map((columnId) => {
              const checked = columnIds.includes(columnId);
              const disabled = checked && columnIds.length === 1;
              return (
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  key={columnId}
                  label={reportColumnLabel(columnId)}
                  onChange={(event) => {
                    const next = event.currentTarget.checked
                      ? [...columnIds, columnId]
                      : columnIds.filter((candidate) => candidate !== columnId);
                    onChange(next);
                  }}
                />
              );
            })}
            {unavailable.size > 0 ? (
              <Text c="dimmed" size="xs">
                Hook and hold columns are hidden while video-metric backfill is in progress. Any selected columns remain in your saved preference.
              </Text>
            ) : null}
          </Stack>
        </div>
      ) : null}
    </div>
  );
}
